import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Toast, ToastType } from '../components/Toast';
import {
    getToastPreferences,
    getToastDuration,
    shouldPersistToast,
    getMaxToasts,
    getToastPosition,
    saveToastPreferences,
    ToastPreferences
} from '../utils/ToastPreferences';

interface ToastOptions {
    duration?: number;
    persist?: boolean;
    action?: {
        label: string;
        onClick: () => void;
    };
    category?: string;
}

const POSITION_STYLES = {
    'top-right': 'top-4 right-4 left-auto bottom-auto',
    'top-left': 'top-4 left-4 right-auto bottom-auto',
    'bottom-right': 'bottom-4 right-4 left-auto top-auto',
    'bottom-left': 'bottom-4 left-4 right-auto top-auto',
};

interface ToastItem {
    id: string;
    message: string;
    type: ToastType;
    options?: ToastOptions;
    createdAt: number;
}

interface ToastContextType {
    showToast: (message: string, type: ToastType, options?: ToastOptions) => string;
    removeToast: (id: string) => void;
    clearToasts: () => void;
    updatePosition: (position: ToastPreferences['position']) => void;
    position: ToastPreferences['position'];
}


const ToastContext = createContext<ToastContextType>({
    showToast: () => '',
    removeToast: () => {},
    clearToasts: () => {},
    updatePosition: () => {},
    position: 'top-right',
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const [position, setPosition] = useState(getToastPosition());

    // Clean up expired toasts
    useEffect(() => {
        const interval = setInterval(() => {
            setToasts(currentToasts => 
                currentToasts.filter(toast => {
                    if (shouldPersistToast(toast.options?.category) || toast.options?.persist) {
                        return true;
                    }
                    const duration = toast.options?.duration || getToastDuration(toast.options?.category);
                    return Date.now() - toast.createdAt < duration;
                })
            );
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    // Update position when preferences change
    useEffect(() => {
        const handleStorageChange = () => {
            setPosition(getToastPosition());
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const showToast = useCallback((message: string, type: ToastType, options?: ToastOptions): string => {
        const id = Math.random().toString(36).substr(2, 9);
        
        setToasts(currentToasts => {
            const newToasts = [...currentToasts];
            const maxToasts = getMaxToasts();
            
            // Remove oldest non-persistent toast if we're at max capacity
            if (newToasts.length >= maxToasts) {
                const index = newToasts.findIndex(t => 
                    !shouldPersistToast(t.options?.category) && !t.options?.persist
                );
                if (index !== -1) {
                    newToasts.splice(index, 1);
                }
            }

            return [
                ...newToasts,
                {
                    id,
                    message,
                    type,
                    options,
                    createdAt: Date.now(),
                }
            ];
        });

        return id;
    }, []);

    const updatePosition = useCallback((newPosition: ToastPreferences['position']) => {
        setPosition(newPosition);
        saveToastPreferences({ position: newPosition });
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id));
    }, []);

    const clearToasts = useCallback(() => {
        setToasts([]);
    }, []);

    const contextValue = useMemo(() => ({
        showToast,
        removeToast,
        clearToasts,
        updatePosition,
        position,
    }), [showToast, removeToast, clearToasts, updatePosition, position]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            <div className={`fixed z-50 ${POSITION_STYLES[position]} mobile-py-safe mobile-px-safe pointer-events-none`}>
                <div className={`flex flex-col${position.includes('bottom') ? '-reverse' : ''} gap-2`}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            show={true}
                            message={toast.message}
                            type={toast.type}
                            action={toast.options?.action}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    return useContext(ToastContext);
}
